import { MessageSquarePlus } from "lucide-react";
import { useState } from "react";

import { cn } from "@/lib/utils";
import type { KaiConversation } from "./kaiTypes";
import {
  conversationStartDate,
  formatConversationAge,
  groupConversationsByPeriod,
} from "./conversationHistoryGroups";
import { KaiConversationTitle } from "./KaiConversationTitle";

export function KaiNewChatButton({
  onClick,
  variant = "plain",
  className,
}: {
  onClick: () => void;
  variant?: "glass" | "plain";
  className?: string;
}) {
  const useGlass = variant === "glass";
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex w-full shrink-0 items-center gap-2 font-medium transition-colors",
        useGlass
          ? "h-8 rounded-lg px-2 text-[13px] text-muted-foreground hover:bg-foreground/[0.04] hover:text-foreground"
          : "h-9 rounded-xl border border-border/60 bg-foreground/[0.03] px-3 text-sm hover:bg-accent/40",
        className,
      )}
    >
      <MessageSquarePlus
        className={cn("shrink-0", useGlass ? "size-3.5" : "size-4")}
      />
      New chat
    </button>
  );
}

interface KaiConversationListProps {
  conversations: KaiConversation[];
  activeId: string | null;
  onSelect: (id: string) => void;
  onNewChat: () => void;
  className?: string;
  /** Hide the full-width New chat button (e.g. when a parent toolbar already has one). */
  showNewChat?: boolean;
  listClassName?: string;
  /** Glass pills on the Kai page sidebar; plain rows inside overlay sheets. */
  variant?: "glass" | "plain";
  /** Parent owns vertical scroll (e.g. {@link KaiHistorySidebar} fade scroller). */
  scrollMode?: "internal" | "external";
}

function ConversationHistoryRow({
  conv,
  isActive,
  startDate,
  useGlass,
  onSelect,
}: {
  conv: KaiConversation;
  isActive: boolean;
  startDate: string;
  useGlass: boolean;
  onSelect: (id: string) => void;
}) {
  const [rowHovered, setRowHovered] = useState(false);

  return (
    <li>
      <button
        type="button"
        onClick={() => onSelect(conv.id)}
        onMouseEnter={() => setRowHovered(true)}
        onMouseLeave={() => setRowHovered(false)}
        onFocus={() => setRowHovered(true)}
        onBlur={() => setRowHovered(false)}
        className={cn(
          "w-full text-left transition-colors",
          useGlass
            ? cn(
                "relative rounded-lg px-2 py-1.5 pl-2.5",
                isActive
                  ? "bg-primary/10 text-foreground before:absolute before:inset-y-1.5 before:left-0 before:w-0.5 before:rounded-full before:bg-primary before:shadow-[0_0_10px_hsl(var(--neon-blue)/0.45)]"
                  : "text-muted-foreground hover:bg-foreground/[0.04] hover:text-foreground",
              )
            : cn(
                "rounded-xl px-3 py-2.5",
                isActive
                  ? "bg-primary/15 text-foreground"
                  : "text-muted-foreground hover:bg-accent/50 hover:text-foreground",
              ),
        )}
      >
        <KaiConversationTitle
          title={conv.title}
          rowHovered={rowHovered}
          className={useGlass ? "text-[13px]" : "text-sm"}
        />
        <p
          className={cn(
            "mt-0.5 tabular-nums text-muted-foreground/75",
            useGlass ? "text-[11px]" : "text-[10px] opacity-60",
          )}
        >
          {formatConversationAge(startDate)}
        </p>
      </button>
    </li>
  );
}

export function KaiConversationList({
  conversations,
  activeId,
  onSelect,
  onNewChat,
  className,
  showNewChat = true,
  listClassName,
  variant = "plain",
  scrollMode = "internal",
}: KaiConversationListProps) {
  const groups = groupConversationsByPeriod(conversations);
  const useGlass = variant === "glass";
  const scrollExternal = scrollMode === "external";

  return (
    <div
      className={cn(
        "flex min-h-0 flex-col",
        useGlass ? "gap-2" : "gap-3",
        className,
      )}
    >
      {showNewChat && (
        <KaiNewChatButton onClick={onNewChat} variant={variant} />
      )}

      <div
        className={cn(
          scrollExternal ? "min-h-0" : "min-h-0 flex-1 overflow-y-auto overscroll-y-contain",
          listClassName,
        )}
      >
        {conversations.length === 0 ? (
          <p className="px-3 py-8 text-center text-xs text-muted-foreground">
            No conversations yet
          </p>
        ) : (
          <div className={cn(useGlass ? "space-y-3" : "space-y-4 pr-2")}>
            {groups.map((group) => (
              <section key={group.period}>
                <h3
                  className={cn(
                    "mb-1 text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground/65",
                    useGlass ? "px-2" : "mb-1.5 px-3 font-semibold tracking-wide text-muted-foreground/80",
                  )}
                >
                  {group.label}
                </h3>
                <ul className={cn(useGlass ? "flex flex-col" : "space-y-0.5")}>
                  {group.conversations.map((conv) => (
                    <ConversationHistoryRow
                      key={conv.id}
                      conv={conv}
                      isActive={conv.id === activeId}
                      startDate={conversationStartDate(conv)}
                      useGlass={useGlass}
                      onSelect={onSelect}
                    />
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
